import * as React from "react";
import type { SingleValue } from "react-select";

import { faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { graphql } from "relay-runtime";

import type { EventFormResult } from "~/calendar/EventForm";
import ImagePicker from "~/common/ImagePicker";
import ThemeContext from "~/common/ThemeContext";
import UserSelector, { type UserSelectorOption } from "~/common/UserSelect/UserSelector";
import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { copyToClipboard, uiAvatar } from "~/lib/utils";
import { environment } from "~/models";
import { EventFormModalWithoutViewer } from "~/nav/panels/Meetings/EventFormModal/EventFormModal";
import { Avatar, Button, Checkbox, useModals } from "~/ui";
import { ButtonLink } from "~/ui/Link";
import toast from "~/ui/toast";

import { DirectReports } from "../DirectReports";
import { NoRecurring1on1Message } from "../NoRecurring1on1Message";
import { UserDeletionConfirmModal } from "../UserDeletionConfirmModal";
import {
	Divider,
	Label,
	Left,
	Right,
	Row,
	SectionTitle,
	SettingsPageSubtitle,
	SettingsPageTitle,
	SubLabel,
	TextFieldDetails,
	TextFieldInput,
	TextFieldLabel,
	type UseSettingUpdaterFunction,
	useUserSetting,
} from "./shared";
import ThemeSelector from "./ThemeSelector";

import type { ProfileGdprExportMutation } from "~/__generated__/ProfileGdprExportMutation.graphql";
import type { ProfileSetImageMutation } from "~/__generated__/ProfileSetImageMutation.graphql";
import type { ProfileSetManagerMutation } from "~/__generated__/ProfileSetManagerMutation.graphql";
import type { ProfileSettingsQuery } from "~/__generated__/ProfileSettingsQuery.graphql";

import styles from "./Profile.module.css";

const { useCallback, useContext, useRef, useState } = React;

// Manually enabled for some customers, see CX-2143
// https://linear.app/fellow/issue/CX-2143/remove-user-settings-manager-field-if-syncing-users-w-okta-[bandish
const MANAGER_LOCK_FEATURE_FLAG = "lock-manager-when-synced";

const profileQuery = graphql`
	query ProfileSettingsQuery {
		viewer {
			activeDelegation {
				id
			}
			user {
				id
				image
				isGuest
				internalAttributes {
					manager {
						id
						fullName
						activeRecurring1on1Event {
							start
							recurrenceRule
						}
						...UserSelect_user @relay(mask: false)
					}
				}
			}
			account {
				isSingleUserAccount
				hrisIntegrated
				primaryAdmin {
					id
					fullName
					email
					image
				}
			}
			settings {
				fullName
				image
				title
				manager
				department
				organization
				managerDisabled
				timezonePreferences {
					preferredTimezone {
						timezone {
							name
							description
							utcOffset
						}
						userLabel
					}
				}
				theme
			}
			...TimezoneSelector_viewer
		}
	}
`;

const profileSetManagerMutation = graphql`
	mutation ProfileSetManagerMutation($input: UserSettingsMutationInput!) {
		updateSettings(input: $input) {
			viewer {
				user {
					internalAttributes {
						manager {
							id
							fullName
							activeRecurring1on1Event {
								start
								recurrenceRule
							}
							...UserSelect_user @relay(mask: false)
						}
					}
				}
				settings {
					managerDisabled
				}
			}
		}
	}
`;

const profileSetImageMutation = graphql`
	mutation ProfileSetImageMutation($input: UserSettingsMutationInput!) {
		updateSettings(input: $input) {
			viewer {
				user {
					image
				}
				settings {
					image
				}
			}
		}
	}
`;

const GDPRDataExportMutation = graphql`
	mutation ProfileGdprExportMutation {
		gdpr {
			result
		}
	}
`;

function setManager(id: string | null, disabled: boolean) {
	commitMutationPromise<ProfileSetManagerMutation>(environment, {
		mutation: profileSetManagerMutation,
		variables: { input: { manager: id, managerDisabled: disabled } },
	});
}
function onManagerChange(value: SingleValue<UserSelectorOption>) {
	setManager(value?.value ?? null, false);
}
function onManagerDisabledChange(checked: boolean) {
	setManager(null, checked);
}

const fullNameUpdater: UseSettingUpdaterFunction<"fullName"> = (store, _response, updatedValue) => {
	const viewerRecord = store.getRoot().getLinkedRecord("viewer");
	if (viewerRecord) {
		viewerRecord.getLinkedRecord("user")?.setValue(updatedValue, "fullName");
		viewerRecord.getLinkedRecord("settings")?.setValue(updatedValue, "fullName");
	}
};

const titleUpdater: UseSettingUpdaterFunction<"title"> = (store, _response, updatedValue) => {
	const viewerRecord = store.getRoot().getLinkedRecord("viewer");
	if (viewerRecord) {
		viewerRecord.getLinkedRecord("user")?.setValue(updatedValue, "title");
		viewerRecord.getLinkedRecord("settings")?.setValue(updatedValue, "title");
	}
};

const departmentUpdater: UseSettingUpdaterFunction<"department"> = (store, _response, updatedValue) => {
	const viewerRecord = store.getRoot().getLinkedRecord("viewer");
	if (viewerRecord) {
		viewerRecord.getLinkedRecord("user")?.setValue(updatedValue, "department");
		viewerRecord.getLinkedRecord("settings")?.setValue(updatedValue, "department");
	}
};

export default function Profile() {
	const modals = useModals();

	const data = useLazyLoadQuery<ProfileSettingsQuery>(profileQuery, {}, { fetchPolicy: "store-and-network" });
	const { viewer } = data,
		{ settings, user } = viewer;

	const [fullName, setFullName] = useUserSetting("fullName", settings.fullName, {
		debounce: true,
		updater: fullNameUpdater,
	});

	const [title, setTitle] = useUserSetting("title", settings.title, {
		debounce: true,
		updater: titleUpdater,
	});

	const [department, _setDepartment] = useUserSetting("department", settings.department, {
		debounce: true,
		updater: departmentUpdater,
	});
	const setDepartment = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => _setDepartment(e.currentTarget.value),
		[_setDepartment],
	);

	const [organization, _setOrganization] = useUserSetting("organization", settings.organization, {
		debounce: true,
		updater: true,
	});
	const setOrganization = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => _setOrganization(e.currentTarget.value),
		[_setOrganization],
	);

	const themeContext = useContext(ThemeContext);
	const [theme, setTheme] = useUserSetting("theme", settings.theme ?? "AUTO", {
		updater: useCallback(
			(store, _response, newValue) => {
				store.getRoot().getLinkedRecord("viewer")?.getLinkedRecord("settings")?.setValue(newValue, "theme");
				themeContext.setTheme(newValue);
			},
			[themeContext],
		),
	});

	const manager = user.internalAttributes?.manager;
	const { managerDisabled } = settings;

	const onEventFormSaved = useCallback(
		(closeModal: () => void) => (result: EventFormResult) => {
			switch (result.type) {
				case "cancelled":
					return closeModal();
				case "succeeded": {
					// Refetch?
					dispatchAnalyticsTrack("[Event Creation Modal] Created Event", {
						// eslint-disable-next-line camelcase
						ux_location: "settings__set_up_1on1_prompt",
						action: "created_recurring_1on1",
					});
					toast.success(`Recurring 1-on-1 ${manager && `with ${manager.fullName} `}was created.`);
					return closeModal();
				}
				default:
					return;
			}
		},
		[manager],
	);

	const onClickSetUp = useCallback(() => {
		dispatchAnalyticsTrack("[Event Creation Modal] Opened Modal", {
			// eslint-disable-next-line camelcase
			ux_location: "settings__set_up_1on1_prompt",
			action: "setup_recurring_1on1",
		});

		modals.open(({ close }) => (
			<React.Suspense fallback={null}>
				<EventFormModalWithoutViewer
					onFormFinish={onEventFormSaved(close)}
					initialData={{
						repeat: "BIWEEKLY",
						participants: manager ? [manager] : [],
						purpose:
							// eslint-disable-next-line max-len
							"The purpose of this 1-on-1 meeting is to review recent successes and challenges, provide feedback, and to set professional development goals.",
					}}
				/>
			</React.Suspense>
		));
	}, [modals, onEventFormSaved, manager]);

	const isManagerLockFeatureEnabled = useFeatureIsOn(MANAGER_LOCK_FEATURE_FLAG);
	const managerSelectorDisabled = viewer.account.hrisIntegrated || isManagerLockFeatureEnabled;

	const [exportStarted, setExportStarted] = useState(false);
	const gdprExport = useCallback(async () => {
		setExportStarted(true);

		const data = await commitMutationPromise<ProfileGdprExportMutation>(environment, {
			mutation: GDPRDataExportMutation,
			variables: {},
		});
		if (data.gdpr?.result) {
			toast.success("Your data export is being prepared and will be emailed to you when complete");
		} else {
			toast.error("Unable to export data. Please try again.");
			setExportStarted(false);
		}
	}, []);

	const openDeleteModal = useCallback(
		() =>
			modals?.open(({ close }) => (
				<UserDeletionConfirmModal canDeleteWorkspace={viewer.account.isSingleUserAccount} onClose={close} />
			)),
		[modals, viewer.account.isSingleUserAccount],
	);

	const setImage = useCallback((image: string | null | undefined) => {
		commitMutationPromise<ProfileSetImageMutation>(environment, {
			mutation: profileSetImageMutation,
			variables: { input: { image: image ?? null } },
		});
	}, []);

	const clearImage = useCallback(() => setImage(null), [setImage]);

	const imagePickerRef = useRef<ImagePicker>(null);
	const uploadImage = useCallback(() => {
		imagePickerRef.current?.dropzone.current?.open();
	}, []);

	return (
		<div className={styles.main}>
			<SettingsPageTitle>Profile</SettingsPageTitle>
			<SettingsPageSubtitle>Manage and customize your profile</SettingsPageSubtitle>
			<Divider />
			<SectionTitle>Personal details</SectionTitle>
			<div className={styles.reducedWidthColumn}>
				<TextFieldLabel>Full name</TextFieldLabel>
				<TextFieldInput
					value={fullName ?? ""}
					onChange={useCallback(e => setFullName(e.currentTarget.value), [setFullName])}
					name="profile_full_name"
				/>
				<TextFieldLabel>Job title</TextFieldLabel>
				<TextFieldInput
					value={title ?? ""}
					onChange={useCallback(e => setTitle(e.currentTarget.value), [setTitle])}
					name="profile_job_title"
				/>
				{user.isGuest ? (
					<>
						<TextFieldLabel>Organization</TextFieldLabel>
						<TextFieldInput value={organization ?? ""} onChange={setOrganization} name="profile_organization" />
					</>
				) : (
					<>
						<TextFieldLabel>Department</TextFieldLabel>
						<TextFieldInput value={department ?? ""} onChange={setDepartment} name="profile_department" />
						<TextFieldDetails>(e.g. Sales, Marketing, Customer Success, etc…)</TextFieldDetails>
						<TextFieldLabel>Manager</TextFieldLabel>
						{managerSelectorDisabled ? (
							<>
								<div className={styles.fakeField}>
									{manager ? (
										<>
											<Avatar user={manager} />
											<span className={styles.fakeFieldContent}>{manager.fullName}</span>
										</>
									) : (
										<span className={cx(styles.fakeFieldContent, styles.placeholder)}>
											{managerDisabled ? "None" : "Not configured"}
										</span>
									)}
								</div>
								<TextFieldDetails>Your manager is configured by your admin and cannot be changed here</TextFieldDetails>
							</>
						) : (
							<>
								<UserSelector
									isClearable
									excludeGuests
									excludeUserIDs={[user.id]}
									doesNotReportUnder={user.id}
									selectedUser={manager ? { value: manager.id, label: manager.fullName, avatar: manager } : null}
									placeholder="Manager name"
									onChange={onManagerChange}
								/>
								<label className={styles.managerToggle}>
									<Checkbox
										className={styles.checkbox}
										checked={!!settings.managerDisabled}
										onCheckedChange={onManagerDisabledChange}
									/>
									&ensp;
									<span className={styles.text}>I don&apos;t have a manager</span>
								</label>
								{manager && !manager.activeRecurring1on1Event && (
									<NoRecurring1on1Message managerName={manager.fullName} onClickSetUp={onClickSetUp} />
								)}
							</>
						)}
					</>
				)}
				<TextFieldLabel>Profile picture</TextFieldLabel>
				<div style={{ display: "flex", gap: "0.5rem 1.5rem", alignItems: "center" }}>
					<ImagePicker
						circular
						desiredWidth={256}
						desiredHeight={256}
						previewWidth={100}
						previewHeight={100}
						placeholder={uiAvatar(settings.fullName ?? "")}
						src={settings.image ?? viewer.user.image ?? undefined}
						onChange={setImage}
						ref={imagePickerRef}
					/>
					<ButtonLink onClick={settings.image ? clearImage : uploadImage}>
						{settings.image ? "Remove photo" : "Upload photo"}
					</ButtonLink>
				</div>
			</div>
			<Divider />
			{user.isGuest ? (
				<>
					<SectionTitle>Preferences</SectionTitle>
					<Row>
						<Left>
							<Label>Interface theme</Label>
							<SubLabel>Select a theme or sync to your system preferences</SubLabel>
						</Left>
						<Right>
							<ThemeSelector value={theme ?? "AUTO"} onChange={setTheme} />
						</Right>
					</Row>
				</>
			) : (
				<>
					<SectionTitle>Direct reports</SectionTitle>
					<div className={styles.reducedWidthColumn} style={{ margin: "1rem 0 1.5rem" }}>
						<DirectReports />
					</div>
				</>
			)}
			<Divider />
			<SectionTitle>Workspace details</SectionTitle>
			<div className={styles.reducedWidthColumn}>
				{data.viewer.account.primaryAdmin && (
					<>
						<TextFieldLabel>Workspace administrator</TextFieldLabel>
						<div className={styles.fakeField}>
							<Avatar user={data.viewer.account.primaryAdmin} />
							<span className={styles.fakeFieldContent}>{data.viewer.account.primaryAdmin.fullName}</span>
						</div>
						<TextFieldDetails>This user can enable integrations for your workspace</TextFieldDetails>
					</>
				)}
				<TextFieldLabel>Workspace URL</TextFieldLabel>
				<div className={styles.fakeField}>
					<span className={styles.fakeFieldContent} style={{ color: "var(--secondaryTextColor)" }}>
						{window.location.origin}
					</span>
					<ButtonLink
						onClick={useCallback(() => {
							copyToClipboard(window.location.origin);
							toast.success("Copied URL to clipboard");
						}, [])}
					>
						<FontAwesomeIcon icon={faLink} /> Copy
					</ButtonLink>
				</div>
				<TextFieldDetails>Share this link with your teammates to access Fellow</TextFieldDetails>
			</div>
			<Divider />
			<SectionTitle>Security and privacy</SectionTitle>
			<Row>
				<Row.Left>
					<Label>Personal data</Label>
					<SubLabel>Export and manage your personal information</SubLabel>
				</Row.Left>
				<Row.Right>
					<Button variant="primary" onClick={gdprExport} disabled={exportStarted}>
						Export
					</Button>
				</Row.Right>
			</Row>
			<Row>
				<Row.Left>
					<Label>Delete your account</Label>
					<SubLabel>Deleting your account will erase all data related to your account</SubLabel>
				</Row.Left>
				<Row.Right>
					<Button variant="destructive" onClick={openDeleteModal} disabled={!!viewer.activeDelegation}>
						Delete account
					</Button>
				</Row.Right>
			</Row>
		</div>
	);
}
